import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { websiteScanApi } from '../lib/api';
import {
  GlobeAltIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ClockIcon,
  ChevronLeftIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; icon: any; label: string }> = {
    PENDING: { bg: 'bg-gray-100', text: 'text-gray-700', icon: ClockIcon, label: 'Pending' },
    CRAWLING: { bg: 'bg-blue-100', text: 'text-blue-700', icon: ArrowPathIcon, label: 'Crawling' },
    ANALYZING: { bg: 'bg-amber-100', text: 'text-amber-700', icon: ArrowPathIcon, label: 'Analyzing' },
    COMPLETED: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircleIcon, label: 'Completed' },
    ERROR: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircleIcon, label: 'Error' },
  };

  const c = config[status] || config.PENDING;
  const Icon = c.icon;

  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${c.bg} ${c.text}`}>
      <Icon className={`h-3.5 w-3.5 ${status === 'CRAWLING' || status === 'ANALYZING' ? 'animate-spin' : ''}`} />
      {c.label}
    </span>
  );
}

function ProgressBar({ current, total }: { current: number; total: number }) {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className="bg-teal-500 h-2 rounded-full transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function WebsiteScanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);

  const isInProgress = (status: string) =>
    ['PENDING', 'CRAWLING', 'ANALYZING'].includes(status);

  const { data: scan, isLoading } = useQuery({
    queryKey: ['website-scan', id],
    queryFn: async () => {
      const response = await websiteScanApi.getScan(id!);
      return response.data;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return isInProgress(status) ? 10000 : false;
    },
  });

  const { data: pageDetail } = useQuery({
    queryKey: ['website-page', id, selectedPageId],
    queryFn: async () => {
      const response = await websiteScanApi.getPageDetail(id!, selectedPageId!);
      return response.data;
    },
    enabled: !!selectedPageId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (!scan) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">Scan not found.</p>
        <Link to="/website-scan" className="text-teal-600 hover:text-teal-700 mt-2 inline-block">
          Back to scans
        </Link>
      </div>
    );
  }

  const results = scan.overallResults as any;
  const pages = scan.pages || [];

  return (
    <div>
      {/* Back link + header */}
      <Link
        to="/website-scan"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        All Scans
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <GlobeAltIcon className="h-6 w-6 text-gray-400" />
            <h1 className="text-xl font-bold text-gray-900 break-all">{scan.url}</h1>
            <StatusBadge status={scan.status} />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Started {formatDistanceToNow(new Date(scan.createdAt), { addSuffix: true })}
            {' · '}Depth: {scan.crawlDepth} · Max pages: {scan.maxPages}
          </p>
        </div>
      </div>

      {/* Progress (while in progress) */}
      {isInProgress(scan.status) && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              {scan.status === 'CRAWLING' ? 'Discovering pages...' : 'Analyzing pages...'}
            </span>
            <span className="text-sm text-gray-500">
              {scan.pagesAnalyzed}/{scan.pagesDiscovered} pages
            </span>
          </div>
          <ProgressBar current={scan.pagesAnalyzed} total={scan.pagesDiscovered || 1} />
        </div>
      )}

      {/* Results Summary (when completed) */}
      {scan.status === 'COMPLETED' && results && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Pages Scanned</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{results.analyzedPages}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Pages with Issues</div>
            <div className={`text-2xl font-bold mt-1 ${results.pagesWithIssues > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {results.pagesWithIssues}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Total Violations</div>
            <div className={`text-2xl font-bold mt-1 ${results.totalViolations > 0 ? 'text-amber-600' : 'text-green-600'}`}>
              {results.totalViolations}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Error Pages</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{results.errorPages}</div>
          </div>
        </div>
      )}

      {/* Top Violations */}
      {results?.topViolations?.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Most Common Issues</h3>
          <div className="space-y-2">
            {results.topViolations.map((v: any) => (
              <div key={v.ruleId} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${
                    v.severity === 'CRITICAL' ? 'bg-red-500' :
                    v.severity === 'HIGH' ? 'bg-orange-500' :
                    v.severity === 'MEDIUM' ? 'bg-amber-500' : 'bg-blue-500'
                  }`} />
                  <span className="text-gray-700">{v.ruleName}</span>
                  <span className="text-xs text-gray-400">{v.ruleId}</span>
                </div>
                <span className="text-gray-500 font-medium">{v.occurrences} pages</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pages List + Detail Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pages List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-900">
                Pages ({pages.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
              {pages.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">
                  {isInProgress(scan.status) ? 'Discovering pages...' : 'No pages found'}
                </div>
              ) : (
                pages.map((page: any) => (
                  <button
                    key={page.id}
                    onClick={() => setSelectedPageId(page.id)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                      selectedPageId === page.id ? 'bg-teal-50 border-l-2 border-teal-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {page.title || 'Untitled'}
                        </p>
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {page.url}
                        </p>
                      </div>
                      <div className="ml-2 flex-shrink-0">
                        {page.status === 'COMPLETED' ? (
                          page.violationCount > 0 ? (
                            <span className="inline-flex items-center gap-0.5 text-xs text-red-600 font-medium">
                              <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                              {page.violationCount}
                            </span>
                          ) : (
                            <CheckCircleIcon className="h-4 w-4 text-green-500" />
                          )
                        ) : page.status === 'ERROR' ? (
                          <XCircleIcon className="h-4 w-4 text-red-400" />
                        ) : (
                          <ArrowPathIcon className="h-4 w-4 text-gray-400 animate-spin" />
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Page Detail Panel */}
        <div className="lg:col-span-2">
          {selectedPageId && pageDetail ? (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {pageDetail.title || 'Untitled Page'}
                  </h3>
                  <a
                    href={pageDetail.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 mt-1"
                  >
                    {pageDetail.url}
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                  </a>
                </div>
                <StatusBadge status={pageDetail.status} />
              </div>

              {/* Violations */}
              {pageDetail.violations && (pageDetail.violations as any[]).length > 0 ? (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Violations ({(pageDetail.violations as any[]).length})
                  </h4>
                  <div className="space-y-3">
                    {(pageDetail.violations as any[]).map((v: any, i: number) => (
                      <div key={i} className="rounded-lg border border-gray-200 p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`inline-block w-2 h-2 rounded-full ${
                              v.severity === 'CRITICAL' ? 'bg-red-500' :
                              v.severity === 'HIGH' ? 'bg-orange-500' :
                              v.severity === 'MEDIUM' ? 'bg-amber-500' : 'bg-blue-500'
                            }`} />
                            <span className="text-sm font-medium text-gray-900">{v.ruleName}</span>
                          </div>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            v.severity === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                            v.severity === 'HIGH' ? 'bg-orange-100 text-orange-700' :
                            v.severity === 'MEDIUM' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {v.severity}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">{v.reason}</p>
                        {v.contextSnippet ? (
                          <div className="mt-2 bg-gray-50 rounded p-2">
                            <p className="text-xs text-gray-500 font-medium mb-1">Found on page:</p>
                            <p className="text-xs text-gray-700 font-mono leading-relaxed whitespace-pre-wrap">
                              {v.contextSnippet.split(/>>>>|<<<</).map((part: string, idx: number) =>
                                idx === 1 ? (
                                  <mark key={idx} className="bg-amber-200 text-amber-900 px-0.5 rounded font-semibold">{part}</mark>
                                ) : (
                                  <span key={idx}>{part}</span>
                                )
                              )}
                            </p>
                          </div>
                        ) : v.detectedText ? (
                          <div className="mt-2 bg-gray-50 rounded p-2">
                            <p className="text-xs text-gray-500 font-medium mb-1">Detected text:</p>
                            <p className="text-xs text-gray-700 font-mono">{v.detectedText}</p>
                          </div>
                        ) : null}
                        <p className="text-xs text-gray-500 mt-2">
                          <span className="font-medium">Required action:</span> {v.requiredAction}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {v.regulation} · {v.ruleId}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : pageDetail.status === 'COMPLETED' ? (
                <div className="text-center py-8">
                  <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" />
                  <p className="mt-2 text-sm font-medium text-green-700">No violations detected</p>
                  <p className="text-xs text-gray-500 mt-1">
                    This page passed all compliance checks
                  </p>
                </div>
              ) : pageDetail.status === 'ERROR' ? (
                <div className="text-center py-8">
                  <XCircleIcon className="mx-auto h-12 w-12 text-red-300" />
                  <p className="mt-2 text-sm font-medium text-red-700">Analysis failed</p>
                  <p className="text-xs text-gray-500 mt-1">{pageDetail.errorMessage}</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <ArrowPathIcon className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
                  <p className="mt-2 text-sm text-gray-500">Analyzing...</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <GlobeAltIcon className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">
                Select a page to view compliance details
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
